import { useState, useEffect } from 'react';
import { adminAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Edit2 } from 'react-feather';
import { getAvailableCurrencies, formatCurrencyWithConversion } from '../utils/currency';
import './AdminUsers.css';

const currencyOptions = getAvailableCurrencies();
const ADMIN_PERMISSION_DEFAULTS = {
  admin_can_manage_users: '1',
  admin_can_manage_balances: '1',
  admin_can_manage_roles: '1',
};

export default function AdminUsers() {
  const { user: currentUser, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState(ADMIN_PERMISSION_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({
    balance: '',
    balance_currency: 'USD',
    balance_action: 'add',
    status: '',
    role: '',
    is_tester: false,
    reason: ''
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    Promise.all([loadUsers(), loadPermissions()]).finally(() => setLoading(false));
  }, []);

  const loadUsers = async () => {
    const res = await adminAPI.users();
    setUsers(res.data.users);
  };

  const loadPermissions = async () => {
    const res = await adminAPI.settings();
    setPermissions(prev => ({ ...prev, ...res.data.settings }));
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const canManageUsers = isSuperAdmin || permissions.admin_can_manage_users !== '0';
  const canManageBalances = isSuperAdmin || permissions.admin_can_manage_balances !== '0';
  const canManageRoles = isSuperAdmin || permissions.admin_can_manage_roles !== '0';

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      balance: '',
      balance_currency: u.currency || 'USD',
      balance_action: 'add',
      status: u.status,
      role: u.role,
      is_tester: u.is_tester === 1 || u.is_tester === true,
      reason: ''
    });
    setMsg('');
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg('');

    try {
      const payload = {
        reason: editForm.reason
      };

      if (canManageUsers) {
        payload.status = editForm.status;
        payload.is_tester = editForm.is_tester;
      }

      if (canManageRoles) {
        payload.role = editForm.role;
      }

      if (canManageBalances && editForm.balance !== '') {
        payload.balance = parseFloat(editForm.balance);
        payload.balance_currency = editForm.balance_currency;
        payload.balance_action = editForm.balance_action;
      }

      await adminAPI.updateUser(editUser.id, payload);
      await loadUsers();

      if (currentUser?.id === editUser.id) {
        await refreshUser();
      }

      const successMessage = editForm.balance !== ''
        ? `User updated successfully. ${editForm.balance_action === 'add' ? 'Added' : 'Reduced'} ${editForm.balance} ${editForm.balance_currency}.`
        : 'User updated successfully.';
      setMsg(successMessage);
      setTimeout(() => setEditUser(null), 1200);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTester = async (u) => {
    if (!canManageUsers) return;
    const newVal = !(u.is_tester === 1 || u.is_tester === true);
    try {
      await adminAPI.updateUser(u.id, { is_tester: newVal, reason: 'Quick tester toggle' });
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update tester status');
    }
  };

  const filtered = users.filter(u =>
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <h1 className="page-title">Manage Users</h1>
      <p className="page-subtitle">{users.length} registered users</p>

      <div className="card">
        <div style={{ marginBottom: 18 }}>
          <input
            className="form-input"
            style={{ maxWidth: 320 }}
            placeholder="Search by username or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : (
          <div className="table-wrap admin-users-table-wrap">
            <table>
              <thead>
                <tr><th>ID</th><th>Username</th><th>Email</th><th>Balance</th><th>Currency</th><th>Role</th><th>Status</th><th>API / VN Access</th><th>Joined</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id}>
                    <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{u.id}</td>
                    <td className="admin-users-username">{u.username}</td>
                    <td className="admin-users-email">{u.email}</td>
                    <td style={{ color: 'var(--green-500)', fontWeight: 600 }}>{formatCurrencyWithConversion(u.balance, u.currency || 'USD', 2)}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{u.currency || 'USD'}</td>
                    <td><span className={`badge badge-${u.role}`}>{u.role}</span></td>
                    <td><span className={`badge badge-${u.status === 'active' ? 'active' : 'canceled'}`}>{u.status}</span></td>
                    <td
                      title={canManageUsers ? 'Click to toggle Virtual Numbers access' : ''}
                      style={{ cursor: canManageUsers ? 'pointer' : 'default' }}
                      onClick={() => handleToggleTester(u)}
                    >
                      {u.is_tester
                        ? <span className="badge badge-active" style={{ background: '#10b981', color: '#fff', userSelect: 'none' }}>✓ Active</span>
                        : <span className="badge badge-canceled" style={{ userSelect: 'none' }}>Disabled</span>
                      }
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="admin-users-actions">
                      <button
                        className="btn btn-secondary btn-sm admin-users-edit-btn"
                        onClick={() => openEdit(u)}
                        disabled={!isSuperAdmin && u.role === 'super_admin'}
                        title={!isSuperAdmin && u.role === 'super_admin' ? 'Only a super admin can edit this account' : 'Edit user'}
                      >
                        <Edit2 size={14} style={{ marginRight: 4 }} /> Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editUser && (
        <div className="modal-overlay" onClick={() => setEditUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Edit User: {editUser.username}</h3>
            {msg && <div className={`alert ${msg.includes('successfully') ? 'alert-success' : 'alert-error'}`}>{msg}</div>}

            <div className="form-group">
              <label className="form-label">Current Balance</label>
              <div style={{ color: 'var(--green-500)', fontWeight: 700 }}>
                {formatCurrencyWithConversion(editUser.balance, editUser.currency || 'USD', 2)}
              </div>
            </div>

            <div className="grid-2" style={{ gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Balance Action</label>
                <select
                  className="form-select"
                  value={editForm.balance_action}
                  onChange={e => setEditForm({ ...editForm, balance_action: e.target.value })}
                  disabled={!canManageBalances}
                >
                  <option value="add">Add (Credit)</option>
                  <option value="reduce">Reduce (Debit)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Amount</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.0001"
                  min="0"
                  placeholder="Leave blank for no balance change"
                  value={editForm.balance}
                  onChange={e => setEditForm({ ...editForm, balance: e.target.value })}
                  disabled={!canManageBalances}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Currency</label>
              <select
                className="form-select"
                value={editForm.balance_currency}
                onChange={e => setEditForm({ ...editForm, balance_currency: e.target.value })}
                disabled={!canManageBalances}
              >
                {currencyOptions.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid-2" style={{ gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} disabled={!canManageUsers}>
                  <option value="active">Active</option>
                  <option value="banned">Banned</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-select" value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })} disabled={!canManageRoles}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  {isSuperAdmin && <option value="super_admin">Super Admin</option>}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input 
                  type="checkbox" 
                  checked={editForm.is_tester} 
                  onChange={e => setEditForm({ ...editForm, is_tester: e.target.checked })}
                  disabled={!canManageUsers}
                />
                Reseller API & Virtual Numbers Access
              </label>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Activates user's Reseller API Key in Settings and grants access to beta services.
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Reason (for audit log)</label>
              <input
                className="form-input"
                placeholder="e.g. Bonus credit, correction..."
                value={editForm.reason}
                onChange={e => setEditForm({ ...editForm, reason: e.target.value })}
              />
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary admin-users-modal-cancel-btn" onClick={() => setEditUser(null)}>Cancel</button>
              <button className="btn btn-primary admin-users-modal-save-btn" onClick={handleSave} disabled={saving || (!canManageUsers && !canManageBalances && !canManageRoles)}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
